<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import {ChartTypeEnum} from  "@/components/explorer/visualizations/ChartTypeEnum.js"
import ToggleSwitch from '@/components/common/ToggleSwitch.vue';
import DropDown from "@/components/common/DropDownSelect.vue"

//properties set by parent
const props = defineProps({
	selectedChartType: {
		type: String,
		required: true,
	},
	indicator: {
		type: Object,
		required: true,
	},
	categories: {
		type: Array,
		required: true
	},
	chartSettings: {
		type: Object,
		required: true
	}
})

//events raised by component
const emit = defineEmits(["openFilters","updateBarChartType", "updateXaxis", 'updateChartCategory'])

//reactive field proxies
const toggleHorizontalBar = ref(false)
const toggleStack = ref(false)
const horizontalBarDisabled = ref(false)
const stackDisabled = ref(true) //TODO: temporarially disabling 
const selectedXAxisCatName = ref('')
const selectedGrpCatName = ref('')
const xAxisCategories = ref([])
const xGrpCategories = ref([])

let unwatchToggleStack = null
let unwatchToggleHorizontal = null

//local variables
let chartOptions = {}
let AllCats = []
let xAxisExcludeList = []
let grpExcludeList = []

function setupFilters() {
	emit('openFilters');
}

function updateXaxis(catName) {
	//let chartSettings = {...props.chartSettings}
	let chartSettings = props.chartSettings
	let xAxisDim = xAxisCategories.value.find(i => i.columnName == catName)
	selectedXAxisCatName.value = catName
	chartSettings.xAxisDim = xAxisDim
	buildXAxisExcludeList()
	buildGrpExcludeList()	
	emit('updateChartCategory', chartSettings);
}

function updateGroup(catName) {
	//let chartSettings = {...props.chartSettings}
	let chartSettings = props.chartSettings
	let oldSeries = chartSettings.seriesDim
	//if series was a parent then reset all values selected, required for parent to work 
	if(oldSeries.childColumnName) {
		oldSeries.appliedValues.forEach(x => x.selected = true)
	}
	let seriesDim = xGrpCategories.value.find(i => i.columnName == catName)
	selectedGrpCatName.value = catName

 	//for new cat, set selections to true for all values except ALL
 	let allVal = seriesDim.totalLabel
	let found = seriesDim.appliedValues.findIndex(i => i.name == allVal)
	seriesDim.appliedValues.forEach((i, x) => {
		if(allVal == null && found == -1) {
			//if no All then select 1st as default
			i.selected = x==0?true:false; 
		} else if(i.name == allVal)
			i.selected = false
		else
			i.selected = true
	})

	//set xaxis series (or group) dim
	chartSettings.seriesDim = seriesDim
	buildXAxisExcludeList()
	buildGrpExcludeList()	
	emit('updateChartCategory', chartSettings);
}

function buildXAxisExcludeList() {
	//Do not allow XAxisCat to be child of GrpCat
	xAxisExcludeList = AllCats.filter(cat => {
		if(cat.columnName == selectedGrpCatName.value || 
		(cat.parentColumnName != null && cat.parentColumnName == selectedGrpCatName.value)) {
			return true;
		}
	}).map(item => item.columnName)
}
function buildGrpExcludeList() {
	//Do not allow GrpCat to be a parent of XAxisCat
	grpExcludeList = AllCats.filter(cat => {
		if(cat.columnName == selectedXAxisCatName.value || 
		(cat.childColumnName != null && cat.childColumnName == selectedXAxisCatName.value)) {
			return true;
		}
	}).map(item => item.columnName)		
}

onUnmounted(() => {
	if(unwatchToggleHorizontal)
		unwatchToggleHorizontal()
	if(unwatchToggleStack)
		unwatchToggleStack()
})

onMounted(() => {

	if(props.chartSettings && props.chartSettings.xAxisDim) {
		selectedXAxisCatName.value = props.chartSettings.xAxisDim.columnName
		selectedGrpCatName.value = props.chartSettings.seriesDim.columnName				
		AllCats = props.categories.filter(cat => {			
			if(cat.categoryLabelFlag == 1 && (cat.childColumnName == null)) {
				return cat
			}
		})
		xAxisCategories.value = AllCats
		xGrpCategories.value = AllCats
		buildXAxisExcludeList()	
		buildGrpExcludeList()

		toggleHorizontalBar.value = props.chartSettings.toggleHorizontalBar
		toggleStack.value = props.chartSettings.toggleStack
		setupWatchers()
	}

	watch(props, (newProps) => {
		if(unwatchToggleHorizontal)
			unwatchToggleHorizontal()
		if(unwatchToggleStack)
			unwatchToggleStack()

		selectedXAxisCatName.value = props.chartSettings.xAxisDim.columnName
		selectedGrpCatName.value = props.chartSettings.seriesDim.columnName				
		AllCats = props.categories.filter(cat => {			
			if(cat.categoryLabelFlag == 1 && (cat.childColumnName == null)) {
				return cat
			}
		})
		xAxisCategories.value = AllCats
		xGrpCategories.value = AllCats

		buildXAxisExcludeList()	
		buildGrpExcludeList()		

		toggleHorizontalBar.value = newProps.chartSettings.toggleHorizontalBar
		toggleStack.value = newProps.chartSettings.toggleStack			
		setupWatchers()
	})

	horizontalBarDisabled.value = !props.indicator.chartTypes.includes(ChartTypeEnum.BARH.apiName) && !props.indicator.chartTypes.includes(ChartTypeEnum.BARHS.apiName)
	//TODO: temporarially disabling stack option
	//stackDisabled.value = !props.indicator.chartTypes.includes(ChartTypeEnum.BARS.apiName) && !props.indicator.chartTypes.includes(ChartTypeEnum.BARHS.apiName)
});

function setupWatchers() {

	unwatchToggleHorizontal = watch(toggleHorizontalBar,(value) => {
		chartOptions= {}
		chartOptions.toggleHorizontalBar = value
		chartOptions.toggleStack = toggleStack.value	
		emit("updateBarChartType", (value?ChartTypeEnum.BARH.chartName:ChartTypeEnum.BAR.chartName), chartOptions)
	});	
	
	unwatchToggleStack = watch(toggleStack,(value) => {
		chartOptions= {}
		chartOptions.toggleHorizontalBar = toggleHorizontalBar.value
		chartOptions.toggleStack = value	

		if(props.selectedChartType == ChartTypeEnum.BARH.chartName || props.selectedChartType == ChartTypeEnum.BARHS.chartName)
			emit("updateBarChartType", (value?ChartTypeEnum.BARHS.chartName:ChartTypeEnum.BARH.chartName), chartOptions)
		else if(props.selectedChartType == ChartTypeEnum.BAR.chartName || props.selectedChartType == ChartTypeEnum.BARS.chartName)
			emit("updateBarChartType",(value?ChartTypeEnum.BARS.chartName:ChartTypeEnum.BAR.chartName), chartOptions)	
	});
}

</script>

<template>
	<div class="chart-options">

		<div class="chart-options-row">
			<div class="chart-options-col">
				<div>
					<div class="label-text">X Axis Categories</div>
				</div>
				<div>
					<DropDown v-model="selectedXAxisCatName" :list="xAxisCategories" @selectedDropdown="updateXaxis" :keyField="'columnName'" :disableFields="xAxisExcludeList" />
				</div>
			</div>

			<div class="chart-options-col">
				<div>
					<div class="label-text">Group</div>
				</div>
				<div>
					<DropDown v-model="selectedGrpCatName" :list="xGrpCategories" @selectedDropdown="updateGroup" :keyField="'columnName'" :disableFields="grpExcludeList" />
				</div>
			</div>
			<div class="chart-options-col bottom">
				<div class="section">
					<div>
						<button @click="setupFilters" class="btn-tertiary"><span class="icon-settings">
							<i class="fa-regular fa-sliders"></i></span>Customize chart</button>
					</div>				
					<div class="toggle-controls">			
						<div class="bar-chart-control">
							<div class="label-text toggle-label">Bar</div>
							<ToggleSwitch :ariaLabel="'Bar'" v-model="toggleHorizontalBar" :disabled="horizontalBarDisabled"/>
						</div>
						<div class="tooltip-wrapper"   :class="[
							{ 'tip-active': stackDisabled },
							'tooltip-dropdown']" data-tooltip="This format is unavailable based on indicator selected.">							
							<div class="bar-chart-control">
									<div class="label-text toggle-label">Stack</div>
									<ToggleSwitch :ariaLabel="'Stack'" v-model="toggleStack" :disabled="stackDisabled"/>
							</div>
						</div>	
					</div>							
				</div>					
			</div>					
		</div>
	</div>
</template>

<style scoped>
.chart-options {
	display:flex;
	flex-direction: row;
	justify-content:space-between;
	margin: 0 1.25rem 0.25rem 1.25rem;	
}

.chart-options-row {
	display:flex;
	flex-direction: row;
	column-gap: var(--spacing-lg);
	width: 100%;
}

.chart-options-col {
	display: flex;
	flex-direction: column;
	justify-content: start;
}

.chart-options-col.bottom {
	justify-content: end;
	width: 100%;
}

.chart-options-col .section {
	display:flex;
	flex-direction: row;
	justify-content: space-between;	
	align-items: center;
}

.toggle-controls {
	display: flex;
	flex-direction: rows;
}

.btn-tertiary {
	padding: 0 0.25rem;
	height: 2.5rem;
	white-space: nowrap
}

.category-hide {
	display: none;
}

.bar-chart-control {
	display: flex;
	align-items: center;
}

.label-text {
	color: var(--text-Secondary);
	font-family: var(--font-family-Title);
	font-weight: var(--font-weight-Medium);
	font-size: var(--font-size-Base);
}

.toggle-label {
	padding-right: .5rem;
	padding-left: 1.5rem;
}

.icon-settings {
	color: var(--icon-cta-Blue);
	margin-right:.5rem;
}

/*** tooltip classes ***/
.tooltip-wrapper {
	position: relative;
	pointer-events: auto; /* enable hover only when disabled */
}

.tooltip-wrapper.tip-active:hover::after {
  	content: attr(data-tooltip);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1em);  
	position: absolute;
	top: -3.75rem;
	left: -6.25rem;
	background: var(--brand-color-MedGrey);
	color: white;
	padding: .25rem .5rem;
	z-index:999;
	line-height:1.25rem;
	border-radius: .25rem;
}

/* First menu item */
.tooltip-wrapper.tooltip-left::after {
  left: 0%;
}

/* Middle menu items */
.tooltip-wrapper.tooltip-center::after {
  left: 50%;
  transform: translateX(-35%);
}

/* Last menu item */
.tooltip-wrapper.tooltip-right::after {
  right: -6.25rem;
}

/* dropdown tooltip */
.viz-menu-container-dropdown .tooltip-wrapper.tip-active[data-v-5e53824a]:hover::after {
	top: -3.75rem;
}

/* dropdown menu item */
.viz-menu-container-dropdown .tooltip-wrapper.tooltip-dropdown::after {
  left: -100%;
}

/* tooltip arrow */
.tooltip-wrapper.tip-active:hover::before {
    content: "";
    position: absolute;

    bottom: calc(100% - 2px); 
    left: 75%;
    transform: translateX(-50%) scale(1.6);

    border-width: 6px;
    border-style: solid;
    border-color: var(--brand-color-MedGrey) transparent transparent transparent;

    z-index: 1000;
}

/* 1023px - iPad */
@media (max-width: 63.9375rem) {
	.chart-options {
		flex-wrap: wrap;
        align-content: space-between;
        /* height: 7.5rem; */		
	}

	.chart-options > div {
		flex: 1 1 50%;
	}

	.bar-chart-control label{
		padding-left: 0;
	}
}

/* 780px - Max container size when Settings panel displayed */
.chart-options {
	container-type: inline-size;
}
@container (max-width: 50.3125rem) {
	.chart-options-row {
		flex-wrap: wrap;
	}
}

/* 767px - mobile */
@media (max-width: 47.9375rem) {
	.chart-options {
        height: 14rem;		
	}

	.chart-options-row {
		flex-wrap: wrap;
	}	

	.chart-options-col {
		flex: 1 1 100%;
		padding-top: 1rem;
	}	
}
</style>